// `/s` ↔ Search V2 adapter. Maps the existing `/s` URL contract to the canonical
// `POST /api/search` request, and a `SearchResultCardDTO` to the listing-shaped object the
// existing `CompactListingCard` renders. Local to LandingPage on purpose: the Store surfaces have
// their own contexts and are not migrated.
//
// Contract: server/search-v2/types.d.ts (SearchV2Request, SearchResultCardDTO),
// docs/search-v2-production-runtime.md.
import { BRANDS } from '../../config/brandRegistry';
import { resolveListingCondition } from '../../util/listingCondition';
import { isWarehouseChannel } from '../../util/itemSourceChannel';
import { parseSelectFilterOptions } from '../../util/search';
import { types as sdkTypes } from '../../util/sdkLoader';

const { Money, UUID } = sdkTypes;

export const SEARCH_V2_PER_PAGE = 20;
export const SEARCH_V2_MAX_QUERY_CHARS = 500;

const DELIVERY_VALUES = ['pickup', 'shipping'];

// Same canonical text normalization as `normText` in server/search-v2/normalize.js:
// NFKC → lowercase → trim → collapse internal whitespace.
const normText = s =>
  String(s)
    .normalize('NFKC')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ');

const unique = values => values.filter((v, i) => values.indexOf(v) === i);

const selectedOptions = raw =>
  raw == null || raw === ''
    ? []
    : parseSelectFilterOptions(String(raw))
        .map(v => v.trim())
        .filter(Boolean);

// Brand (server normalizeBrand): a registry `value` or display `name` resolves to the registry
// value; anything else to its normalized text.
export const normalizeBrandFilterValue = raw => {
  const n = normText(raw);
  if (!n) return null;
  const hit = BRANDS.find(b => normText(b.value) === n || normText(b.name) === n);
  return hit ? normText(hit.value) : n;
};

// Condition: the repo's resolver, accepting the same canonical and legacy listing-type spellings
// the legacy `/s` filter accepted.
const canonicalCondition = value =>
  resolveListingCondition({ condition: value, listingType: value });

const anyOf = values => (values.length ? { any_of: values } : null);

// URL price is "min,max" in dollars. The legacy Sharetribe price filter has an exclusive max; the
// Search V2 range is closed, so max becomes max − 1 cent. A degenerate range (min = max, which the
// legacy filter answered with nothing) keeps the single price instead of becoming invalid.
const priceRange = raw => {
  if (raw == null || raw === '') return null;
  const parts = String(raw).split(',');
  if (parts.length !== 2 || parts.some(p => p.trim() === '')) return null;
  const [min, max] = parts.map(Number);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
  const minCents = Math.round(min * 100);
  const maxCents = Math.max(minCents, Math.round(max * 100) - 1);
  return { min: minCents, max: maxCents };
};

const sortSpec = (sortValue, query) => {
  switch (sortValue) {
    case 'price-low':
      return { kind: 'price', dir: 'asc' };
    case 'price-high':
      return { kind: 'price', dir: 'desc' };
    case 'relevance':
      return query ? { kind: 'relevance' } : { kind: 'created_at', dir: 'desc' };
    default:
      return { kind: 'created_at', dir: 'desc' };
  }
};

/**
 * Sanitized `/s` URL params + sort selection → canonical Search V2 request (no cursor, no facets).
 * Only the mapped params are read, so any other URL parameter leaves the request unchanged.
 */
export const buildSearchV2Request = (params = {}, sortValue = 'newest') => {
  const keywords = params.keywords == null ? '' : String(params.keywords).trim();
  const query = keywords ? keywords.slice(0, SEARCH_V2_MAX_QUERY_CHARS) : null;

  const filters = {};
  const category = anyOf(unique(selectedOptions(params.pub_category)));
  if (category) filters.category = category;
  const subcategory = anyOf(unique(selectedOptions(params.pub_subcategory)));
  if (subcategory) filters.subcategory = subcategory;
  const brand = anyOf(
    unique(
      selectedOptions(params.pub_brand)
        .map(normalizeBrandFilterValue)
        .filter(Boolean)
    )
  );
  if (brand) filters.brand = brand;
  const condition = anyOf(
    unique(
      selectedOptions(params.pub_condition)
        .map(canonicalCondition)
        .filter(Boolean)
    )
  );
  if (condition) filters.condition = condition;
  const delivery = anyOf(
    unique(
      selectedOptions(params.pub_deliveryOption)
        .map(v => v.toLowerCase())
        .filter(v => DELIVERY_VALUES.includes(v))
    )
  );
  if (delivery) filters.delivery = delivery;
  const price = priceRange(params.price);
  if (price) filters.price_cents = price;

  const isIframe = params.iframe === true || params.iframe === 'true';
  return {
    query,
    context: {
      catalog_surface: 'global_marketplace',
      presentation_context: isIframe ? 'iframe' : 'standalone',
      ...(isWarehouseChannel(params.channel) ? { channel: 'warehouse' } : {}),
    },
    filters,
    sort: sortSpec(sortValue, query),
    per_page: SEARCH_V2_PER_PAGE,
  };
};

/** Stable identity of a request without its cursor: equal keys = the same search. */
export const searchV2RequestKey = request => JSON.stringify(request);

/** Brand facet counts → the lowercase values BrandCarousel matches against (null = unknown). */
export const brandValuesFromFacets = facets => {
  const counts = facets?.brand;
  if (!counts || typeof counts !== 'object') return null;
  return Object.keys(counts).filter(value => counts[value] > 0);
};

/**
 * SearchResultCardDTO → the minimal listing shape CompactListingCard reads. The card derives the
 * slug from the title (createSlug) and applies the customer commission to the base price itself,
 * exactly as for Sharetribe listings.
 */
export const searchV2CardToListing = card => {
  const { listing_id, title, image, brand, price, seller, condition, delivery } = card;
  const hasPrice = Number.isSafeInteger(price?.base_amount) && !!price?.currency;
  const publicData = {
    listingType: card.listing_type,
    source: card.source,
    pickupEnabled: !!delivery?.pickup,
    shippingEnabled: !!delivery?.shipping,
  };
  if (brand?.display_value) publicData.brand = brand.display_value;
  if (condition) publicData.condition = condition;
  if (seller?.location_label) publicData.dealerLocation = seller.location_label;

  return {
    id: new UUID(listing_id),
    type: 'listing',
    attributes: {
      title: title || '',
      price: hasPrice ? new Money(price.base_amount, price.currency) : null,
      publicData,
    },
    author: seller?.id
      ? {
          id: new UUID(seller.id),
          type: 'user',
          attributes: {
            profile: {
              displayName: seller.name || '',
              publicData: seller.dealer_name ? { businessName: seller.dealer_name } : {},
            },
          },
        }
      : null,
    images: image?.url
      ? [{ type: 'image', attributes: { variants: { 'listing-card': { url: image.url } } } }]
      : [],
  };
};
